import { Squares2X2Icon, PhotoIcon, BellIcon, PuzzlePieceIcon, DocumentTextIcon, DocumentDuplicateIcon, UsersIcon, UserGroupIcon, CreditCardIcon, Cog6ToothIcon, BanknotesIcon, BuildingOffice2Icon, } from '@heroicons/react/24/outline';
/**
 * Build the platform sidebar sections for a given auth user. MSP and
 * Reseller sections are defined but filtered out at V1 because org.type
 * is always STANDALONE today. When MSP/reseller channels light up later,
 * the gating logic here is the only thing that has to flip.
 */
export function platformSidebarConfig(user) {
    const orgType = resolveOrgType(user);
    const sections = [
        {
            title: 'Overview',
            items: [
                { label: 'Dashboard', to: '/', Icon: Squares2X2Icon },
                // My Products count will eventually come from a subscriptions hook.
                // Hardcoded to 1 (SSL) today since that's the only SUBSCRIBED entry
                // in productCatalog — see productCatalog.ts.
                {
                    label: 'My Products',
                    to: '/my-products',
                    Icon: PhotoIcon,
                    badge: { text: '1', kind: 'count' },
                },
            ],
        },
        {
            title: 'Alerts & Comms',
            items: [
                {
                    label: 'Alert Providers',
                    to: '/alerts/providers',
                    Icon: BellIcon,
                    // Badge is populated dynamically by PlatformShell from a live
                    // GET /sslwidewatch/alerts/providers query — was previously
                    // hardcoded to '7' which made it look like there were always
                    // seven configured even when the org had only one.
                },
                {
                    label: 'Alert Groups',
                    to: '/alerts/groups',
                    Icon: UserGroupIcon,
                    // Badge populated by PlatformShell, same pattern as providers.
                },
                {
                    label: 'Alert Templates',
                    to: '/alerts/templates',
                    Icon: DocumentDuplicateIcon,
                },
                { label: 'Integrations', to: '/alerts/integrations', Icon: PuzzlePieceIcon },
                { label: 'Delivery Log', to: '/alerts/delivery-log', Icon: DocumentTextIcon },
            ],
        },
        {
            title: 'Organization',
            items: [
                { label: 'Team', to: '/team/users', Icon: UsersIcon },
                { label: 'Billing', to: '/billing', Icon: CreditCardIcon },
                { label: 'Settings', to: '/settings/organization', Icon: Cog6ToothIcon },
            ],
        },
        // ---- MSP-only section (gated off at V1) ----
        {
            title: 'My Clients',
            items: [
                {
                    label: 'Clients',
                    to: '/msp/clients',
                    Icon: BuildingOffice2Icon,
                    visibleFor: ['MSP'],
                },
                {
                    label: 'Billing rollup',
                    to: '/msp/billing',
                    Icon: CreditCardIcon,
                    visibleFor: ['MSP'],
                },
            ],
        },
        // ---- Reseller-only section (gated off at V1) ----
        {
            title: 'Reseller Portal',
            items: [
                {
                    label: 'Referrals',
                    to: '/reseller/customers',
                    Icon: UsersIcon,
                    visibleFor: ['RESELLER'],
                },
                {
                    label: 'Commissions',
                    to: '/reseller/commissions',
                    Icon: BanknotesIcon,
                    visibleFor: ['RESELLER'],
                },
            ],
        },
    ];
    // Filter items by org type, then drop any section that ends up empty.
    return sections
        .map((s) => ({
        ...s,
        items: s.items.filter((i) => !i.visibleFor || i.visibleFor.includes(orgType)),
    }))
        .filter((s) => s.items.length > 0);
}
/**
 * Temporary shim — the SB3 backend doesn't yet return org.type on the
 * signin payload. Once the auth endpoint starts returning it (new backend
 * migration ticket), this just reads `user.orgType`. Until then every org
 * is treated as STANDALONE, which is the correct V1 default.
 */
function resolveOrgType(user) {
    // Best-effort read — AuthUser doesn't declare orgType today, but once
    // the backend starts sending it we can pick it up without a schema change.
    const raw = user?.orgType;
    if (raw === 'MSP' || raw === 'MSP_CLIENT' || raw === 'RESELLER')
        return raw;
    return 'STANDALONE';
}
